<template>
  <div class="map__overlay__holder">
    <div id="map" class="map map--stretch"></div>
    <div class="map__overlay" v-if="loading">
      <v-progress-circular
        color="primary"
        size="50"
        indeterminate
      ></v-progress-circular>
    </div>
    <div v-if="error" class="map__error white primary--text d-flex align-center justify-center">
      Something went wrong with the map try again later
    </div>
  </div>
</template>

<script>
// import axios from 'axios';
import { mapState } from 'vuex';
import Leaflet from 'leaflet';
import { Countries } from '@/utils';
import { Geocoding } from '@/api';
// import iot from '@/helpers/iot-data';
// import { simplify, metersToDegrees } from '@/helpers';
import {
  // chain,
  get,
  keys,
  isEmpty,
  // cloneDeep,
  values,
  entries,
} from 'lodash';

const LEFT = 1;
const RIGHT = 2;
const Markers = {
  TRUCK: 1,
  FLIGHT: 2,
};

/* eslint-disable */
function nonRepeated(acc, hit) {
  const tracker = get(hit, '_source.tracker.location');
  const air = get(hit, '_source.air.location');
  const _h = values(tracker || air);

  if (!acc.some(h => {
    const t = get(h, '_source.tracker.location');
    const a = get(h, '_source.air.location');
    const s = values(a || t);
    return s[0] === _h[0] || s[1] === _h[1]
  })) {
    acc.push(hit);
  }

  return acc;
}
/* eslint-enable */

export default {
  name: 'app-map',
  props: {
    siteA: Object,
    siteB: Object,
    dest: Object,
    iot: Array,
    update: String,
    done: {
      default: false,
      type: Boolean,
    },
  },
  data() {
    return {
      loading: false,
      layer: {},
      lines: [],
      points: [],
      markers: [],
      hits: [],
      direction: 0,
      error: false,
    };
  },
  watch: {
    update() {
      this.draw();
    },
  },
  computed: {
    ...mapState({
      scope: 'Scope',
    }),
  },
  methods: {
    oldIotParse() {
      const hits = [].concat(
        get(this, 'iot.0.trackers.0.hits', []).slice(),
        get(this, 'iot.1', []).slice(),
      )
        .filter(h => get(h, '_source.timeOfReport'))
        .sort((a, b) => (
          get(a, '_source.timeOfReport') > get(b, '_source.timeOfReport') ? 1 : -1
        ))
        .reduce(nonRepeated, [])
        .map(h => [
          get(h, '_source.sourceType'),
          values(
            get(h, '_source.tracker.location')
            || get(h, '_source.air.location'),
          ),
        ])
        .filter(([, latlng]) => latlng.length);


      return hits
        .reduce((acc, current, i) => {
          const [type, latlng] = current;
          const [, next] = hits[i + 1] || [];

          acc.push({
            type,
            from: latlng,
            to: next || [],
          });

          return acc;
       }, []);
    },
    setOrientation() {
      // Check if there is some tracker hits.
      if (this.hits && this.hits.length) {
        const first = this.hits.slice().shift();
        const start = first.from;

        this.hits
          .filter(hit => (
            hit.from
            && hit.from.length
            && hit.to
            && hit.to.length
          ))
          .map((hit) => {
            const from = Leaflet.latLng(hit.from[0], hit.from[1]);
            const to = Leaflet.latLng(hit.to[0], hit.to[1]);

            this.checkAntimeridian(start, from, true);
            this.checkAntimeridian(start, to, true);
            return true;
          });
      }
    },
    /**
     * When we have missing points for middle path, it's hard to now if the line
     * should go to the left or right, depending on the start and finish points,
     * this method tries to fix this.
     *
     * Due to some wrong path drawing in some scenarios, like: 01H6X5Y.
     * We should only return the next point without calculation if it's crossing
     * the meridian.
     *
     * TODO -
     * Find a solution for wrong display path when we few middle points.
     */
    checkAntimeridian(from, to) {
      return to;
      // if (from) {
      //   const prev = from.lng || 0;
      //   const next = to.lng || 0;
      //   const point = cloneDeep(to);
      //   const direction = this.setNextRound(next - prev);

      //   if (direction >= 180) {
      //     point.lng -= 360;
      //     if (orientation) this.direction = LEFT;
      //   }

      //   if (direction <= -180) {
      //     point.lng += 360;
      //     if (orientation) this.direction = RIGHT;
      //   }

      //   return point;
      // }

      // return to;
    },
    setNextRound(n) {
      const a = (Math.round(n / 10) * 10);
      const b = a + 10;
      return (n - a > b - n) ? b : a;
    },
    setMarker(latlng, type, isFirst) {
      let icon = Leaflet.divIcon({
        html: '<i class="mdi mdi-map-marker map__icon red--text"></i>',
        iconSize: [30, 30],
        iconAnchor: [15, 26],
        className: 'map__icon-wrapper',
      });
      let marker = Leaflet.marker(latlng, { icon });

      if (type === Markers.TRUCK && !this.done) {
        icon = Leaflet.divIcon({
          html: '<i class="map__icon"><img src="/images/truck.png"/></i>',
          iconSize: [30, 30],
          iconAnchor: [15, 26],
          className: 'map__icon-wrapper',
        });

        marker = Leaflet.marker(latlng, { icon });
        this.markers.push(marker);
        marker.addTo(this.map);
      }

      if (type === Markers.FLIGHT) {
        icon = Leaflet.divIcon({
          html: `<i class="map__icon"><img src="/images/${isFirst ? 'takeoff-right' : 'takeoff-left'}.png"/></i>`,
          iconSize: [30, 30],
          iconAnchor: [15, 26],
          className: 'map__icon-wrapper',
        });
      }

      marker = Leaflet.marker(latlng, { icon });
      this.markers.push(marker);
      marker.addTo(this.map);
    },
    async search(params, retry = 0) {
      const q = entries({
        street: params.street,
        city: params.city,
        state: params.state,
        country: Countries[params.country],
        postalcode: params.postalcode,
      })
        .filter(([, v]) => v)
        .reduce((acc, [k, v]) => {
          acc[k] = v;
          return acc;
        }, {});
      let data = {};
      let points = [];

      if (retry === 0 && retry < 2) {
        // Mapbox
        try {
          ({ data } = await Geocoding.get(
            `/${values(q).join(',')}.json`,
            {
              access_token: process.env.VUE_APP_MAPBOX_TOKEN,
              format: 'geojson',
            },
            process.env.VUE_APP_MAPBOX_GEO,
          ));

          ({ features: points } = data);
        } catch (e) {
          return this.search(params, retry + 1);
        }
      } else {
        this.error = true;
      }

      if (!isEmpty(q) && !points.length) {
        const p = {};
        const ks = keys(q);

        // This lines seems to unnecesary, but in some cases the nominatim (no longer used) app
        // does not find the countries or cities, to avoid that we remove the
        // external parameters until get the high priority params at the center
        // of the array of params.
        if (ks.length) ks.shift();
        if (!ks.length) return null;

        ks.map((k) => {
          p[k] = q[k];
          return true;
        });

        // params.country is the keyword instead of country's name.
        p.country = params.country;

        return this.search(p);
      }

      return points[0];
    },
    retrieveLatLon(res) {
      if (res.center) {
        return {
          lat: res.center[1],
          lon: res.center[0],
        };
      }

      // Mapbox api response:
      // https://docs.mapbox.com/api/search/geocoding/#example-response-forward-geocoding
      if (res.geometry?.coordinates) {
        return {
          lat: res.geometry.coordinates[1],
          lon: res.geometry.coordinates[0],
        };
      }

      return res;
    },
    checkTile(tilesURL) {
      let url = tilesURL;
      url = url.replace('{z}', '0');
      url = url.replace('{x}', '0');
      url = url.replace('{y}', '0');

      return new Promise((resolve) => {
        window.fetch(url)
          .then(() => resolve(true))
          .catch(() => resolve(false));
      });
    },
    async initMap() {
      if (this.map) return;

      const [trace, mapbox, open] = await Promise.all([
        /* this.checkTile(process.env.VUE_APP_TILES), */
        Promise.resolve(false),
        this.checkTile(`${process.env.VUE_APP_MAPBOX_TILES}?access_token=${process.env.VUE_APP_MAPBOX_TOKEN}`),
        this.checkTile(process.env.VUE_APP_OPEN_TILES),
      ]);

      if (trace) {
        this.layer = [
          Leaflet.tileLayer(process.env.VUE_APP_TILES, {
            attribution: (
              '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            ),
          }),
          Leaflet.tileLayer(process.env.VUE_APP_LABEL_TILES, {
            attribution: (
              '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            ),
          }),
        ];
      } else if (mapbox) {
        this.layer = Leaflet.tileLayer(`${process.env.VUE_APP_MAPBOX_TILES}?access_token={access_token}`, {
          attribution: (
            'Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> '
            + 'contributors, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>'
          ),
          maxZoom: 18,
          id: 'mapbox/sreets-v11',
          tileSize: 512,
          zoomOffset: -1,
          access_token: process.env.VUE_APP_MAPBOX_TOKEN,
        });
      } else if (open) {
        this.layer = Leaflet.tileLayer(process.env.VUE_APP_OPEN_TILES, {
          attribution: (
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          ),
        });
      } else {
        this.error = true;
      }

      // [Info]
      // For some reason the routes .search and .details overlap so to avoid getting the error "the
      // container is already initialized" making a reference to the map, we store the Leaflet.map
      // reference in a global scope, later on we delete this reference to avoid problems with other
      // places were we used the map (cuz it's not the same map, only in this case is the same).
      if (!window._map) {
        window._map = Leaflet.map('map', {
          layers: this.layer,
          attributionControl: true,
        });
      }

      this.map = window._map;
      this.map.setZoom(6);
    },
    async point(points, site) {
      const { state = '' } = site;
      let stateValue = '';

      if (state.match(/\b\D{2}\b/g) && this.scope.name === 'IBM') {
        [stateValue] = state.match(/\b\D{2}\b/g);
      }

      if (!isEmpty(site)) {
        const res = await this.search({
          // postalcode: site.postalCode,
          state: stateValue,
          street: site.address,
          city: site.city,
          country: site.country,
        });
        let point = [];
        let lat = 0;
        let lon = 0;

        if (!res) return;
        ({ lat, lon } = this.retrieveLatLon(res));

        point = Leaflet.latLng(lat, lon);
        points.push(point);
      }
    },
    clear() {
      this.markers.map(m => m.remove());
      this.lines.map(l => l.remove());
      this.points = [];
      this.direction = 0;
    },
    async setHits() {
      this.hits = this.oldIotParse();
      // this.hits = !window.ENV.iot.parse
      //   ? this.oldIotParse()
      //   : (await iot.parse(this.iot)).data;
    },
    async draw() {
      await this.setHits();
      this.clear();
      this.setOrientation();
      if (this.iot && !this.siteA && !this.siteB) {
        this.drawIOT();
        this.drawFinalDestination();
      } else if (this.siteA || this.siteB) {
        this.drawSites();
      }
    },
    drawLine(points, { color = '#3200E6', dashArray = '0', dashOffset = '0' } = { }) {
      const line = Leaflet.polyline(points, {
        opacity: 0.8,
        color,
        dashArray,
        dashOffset,
      });
      line.addTo(this.map);
      this.lines.push(line);
    },
    async drawSites() {
      const points = [];
      this.loading = true;

      // Put markers on the map.
      if (this.siteA) await this.point(points, this.siteA);
      if (this.siteB) await this.point(points, this.siteB);
      this.loading = false;

      if (points.length > 1) {
        const from = points[0];
        const to = points[1];
        const direction = this.setNextRound(to.lng - from.lng);

        if (direction >= 180 || this.direction === LEFT) {
          points[1].lng -= 360;
        } else if (direction <= -180 || this.direction === RIGHT) {
          points[1].lng += 360;
        }

        points.map(p => this.setMarker(p));
        this.drawLine(points);
        this.map.fitBounds(points);
      } else if (points.length) {
        points.map(p => this.setMarker(p));
        this.map.setView(points[0], 4);
      }
    },
    async drawIOT() {
      this.loading = true;
      let hits = this.hits.slice();

      if (hits.length > 1) {
        hits = hits.filter(h => (
          h.from
          && h.from.length
          && h.to
          && h.to.length
        ));
      }

      if (hits.length) {
        const first = hits.slice().shift();
        const last = hits.slice().pop();
        const flights = hits.slice().filter(h => h.type === 'air');
        const firstAir = flights.shift();
        const lastAir = flights.pop();
        // const middleAir = flights[Math.floor(flights.length / 2)];
        const start = Leaflet.latLng(first.from[0], first.from[1]);

        if (first && first.from.length) {
          const device = first.from;
          this.setMarker(Leaflet.latLng(device[0], device[1]));
        }

        if (last && last.to.length) {
          const device = last.to;
          this.setMarker(
            this.checkAntimeridian(
              start,
              Leaflet.latLng(device[0], device[1]),
            ),
            1,
          );
        }

        if (firstAir) {
          const [from, to] = firstAir.from;
          this.setMarker(Leaflet.latLng(from, to), Markers.FLIGHT, true);
        }

        if (lastAir) {
          const [from, to] = lastAir.to;
          this.setMarker(Leaflet.latLng(from, to), Markers.FLIGHT);
        }

        // if (middleAir) {
        //   const [from, to] = middleAir.from;
        //   this.setMarker(Leaflet.latLng(from, to), Markers.FLIGHT);
        // }

        hits.map((hit) => {
          const path = [];
          let color = '';
          const from = Leaflet.latLng(hit.from[0], hit.from[1]);
          const to = Leaflet.latLng(hit.to[0], hit.to[1]);

          // [deprecated]
          // if (!from) {
          //   let i = index;

          //   while (hits[i] && !from) {
          //     i -= 1;
          //     const h = hits[i];
          //     from = Leaflet.latLng(
          //       get(h.from.air || h.from.tracker, 'location.lat'),
          //       get(h.from.air || h.from.tracker, 'location.lon'),
          //     );
          //   }
          // }

          // if (!to) {
          //   let i = index;

          //   while (hits[index] && !to) {
          //     i += 1;
          //     const h = hits[i];
          //     to = Leaflet.latLng(
          //       get(h.to.air || h.to.tracker, 'location.lat'),
          //       get(h.to.air || h.to.tracker, 'location.lon'),
          //     );
          //   }
          // }

          // Convert distance from meters to kilometers.
          // const distance = from.distanceTo(to).toFixed(0) / 1000;

          if (hit.type === 'tracker') color = '#E62E2D';
          if (hit.type === 'air') color = '#3200E6';
          if (from) path.push(this.checkAntimeridian(start, from));
          if (to) path.push(this.checkAntimeridian(start, to));

          this.points.push(...path);
          this.drawLine(path, { color });

          return true;
        });

        // Sometimes we have an empty array for tracker or air.
        if (this.points.length) this.map.fitBounds(this.points);
      } else {
        this.map.setView([0, 0], 0);
      }

      this.loading = false;
    },
    async drawFinalDestination() {
      if (!this.done && this.dest) {
        const { state = '' } = this.dest;
        let stateValue = '';
        let lat = 0;
        let lon = 0;

        if (state.match(/\b\D{2}\b/g) && this.scope.name === 'IBM') {
          [stateValue] = state.match(/\b\D{2}\b/g);
        }

        const res = await this.search({
          // postalcode: this.dest.postalCode,
          state: stateValue,
          street: this.dest.address,
          city: this.dest.city,
          country: this.dest.country,
        });

        if (!res) return;
        ({ lat, lon } = this.retrieveLatLon(res));

        const dest = Leaflet.latLng(lat, lon);
        const last = Leaflet.latLng(this.hits.slice().pop().from);

        this.drawLine([dest, last], { dashArray: '10, 10', dashOffset: '10' });
        this.setMarker(dest);
        this.points.push(dest);
        this.map.fitBounds(this.points);
      }
    },
  },
  async mounted() {
    await this.initMap();
    this.draw();
  },
  destroyed() {
    window._map = null;
  },
};
</script>
